import { useCallback, useEffect, useMemo, useState } from "react";
import ui from "../components/ui.module.css";
import { toActivity } from "../data/activityAdapter";
import { resolvePolicy } from "../data/shared/responseEvaluation";
import { redeemTeacherPreview } from "../data/teacherPreviewRepository";
import useLocalSession from "../hooks/useLocalSession";
import Activity from "./Activity";
import Checking from "./Checking";

// What a used launch code becomes in the address bar (see App).
export const PREVIEW_OPENED = "opened";

// A launch code can be redeemed only once, so each code is sent once however
// often the effect below runs (React runs it twice in development).
const redemptions = new Map();
const redeemOnce = (code) => {
  if (!redemptions.has(code)) redemptions.set(code, redeemTeacherPreview(code));
  return redemptions.get(code);
};

const Message = ({ title, children, action, alert = false }) => (
  <main className={`${ui.frame} ${ui.center}`}>
    <div className={`${ui.card} ${ui.stack}`} role={alert ? "alert" : undefined}>
      <h1>{title}</h1>
      <p>{children}</p>
      {action}
    </div>
  </main>
);

// The canonical renderers and step lifecycle, against an attempt held only in
// memory: nothing is sent anywhere and no learner record is created. Feedback
// follows the activity's own live policy, as a learner would see it.
const PreviewActivity = ({ activity, onFinished, onUnavailable }) => {
  const policy = useMemo(
    () => resolvePolicy(activity.definition, "live") || resolvePolicy({}, "live"),
    [activity]
  );
  const session = useLocalSession(activity, policy);
  const { phase } = session;
  useEffect(() => {
    if (phase === "finished") onFinished();
    if (phase === "unavailable") onUnavailable();
  }, [phase, onFinished, onUnavailable]);
  if (phase !== "activity") return <Checking />;
  return <Activity session={session} />;
};

const TeacherPreview = ({ code }) => {
  const [state, setState] = useState(() =>
    code === PREVIEW_OPENED ? { phase: "closed" } : { phase: "loading" }
  );

  useEffect(() => {
    if (code === PREVIEW_OPENED) return undefined;
    let active = true;
    redeemOnce(code).then(
      (record) => active && setState({ phase: "activity", activity: toActivity(record), run: 1 }),
      () => active && setState({ phase: "error" })
    );
    return () => {
      active = false;
    };
  }, [code]);

  const finished = useCallback(
    () => setState((current) => ({ phase: "complete", activity: current.activity, run: current.run })),
    []
  );
  const unavailable = useCallback(() => setState({ phase: "error" }), []);

  if (state.phase === "loading") return <Checking />;
  if (state.phase === "complete") {
    return (
      <Message
        title="Preview finished"
        action={
          <button
            type="button"
            className={`${ui.button} ${ui.secondary}`}
            onClick={() =>
              setState({ phase: "activity", activity: state.activity, run: (state.run || 1) + 1 })
            }
          >
            Preview again
          </button>
        }
      >
        This is where learners are asked to look at their teacher. No learner work was saved.
      </Message>
    );
  }
  if (state.phase === "closed") {
    return (
      <Message title="Preview closed">
        Open the preview again from the lesson.
      </Message>
    );
  }
  if (state.phase === "error") {
    return (
      <Message title="Preview unavailable" alert>
        Close this preview and try again from the lesson.
      </Message>
    );
  }
  return (
    <>
      <aside className={ui.previewBanner}>Teacher preview · nothing is saved</aside>
      <PreviewActivity
        key={state.run}
        activity={state.activity}
        onFinished={finished}
        onUnavailable={unavailable}
      />
    </>
  );
};

export default TeacherPreview;
